import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Award, Gauge, MapPin, Clock, Leaf, Trophy, Car, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";

function scoreBand(eco) {
  if (eco >= 85) return { label: "Eco champion", color: "#2A9D8F", bg: "#E5DFD3", icon: Trophy };
  if (eco >= 70) return { label: "Smooth driver", color: "#2A9D8F", bg: "#EBE7E0", icon: Leaf };
  if (eco >= 50) return { label: "Good trip", color: "#C05A3A", bg: "#EBE7E0", icon: Award };
  return { label: "Easy on the throttle next time", color: "#A33B3B", bg: "#F9F8F6", icon: Award };
}

export default function PublicTripScore() {
  const { bookingId } = useParams();
  const [score, setScore] = useState(null);
  const [error, setError] = useState(null);
  useEffect(() => {
    api.get(`/trip-score/${bookingId}/share`)
      .then(({ data }) => setScore(data))
      .catch(() => setError("Trip not found or no telemetry available."));
  }, [bookingId]);

  if (error) return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4">
      <Card className="border-border p-8 max-w-md text-center">
        <Car className="mx-auto text-muted-foreground mb-2" size={32} />
        <div className="font-display text-xl font-semibold">No trip found</div>
        <p className="text-sm text-muted-foreground mt-1">{error}</p>
        <Button asChild className="mt-4 bg-primary hover:bg-primary/90"><Link to="/">Back to RydeXpress</Link></Button>
      </Card>
    </div>
  );
  if (!score) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Loading…</div>;

  const band = scoreBand(score.eco_score);
  const Icon = band.icon;
  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center gap-2 justify-center mb-6">
          <div className="w-9 h-9 rounded-lg bg-primary flex items-center justify-center">
            <Car className="text-primary-foreground" size={18} />
          </div>
          <div className="font-display text-lg font-bold tracking-tight">{score.platform_name}</div>
        </Link>

        <div
          className="rounded-lg p-8 grain-overlay relative overflow-hidden shadow-organic"
          style={{ background: band.bg }}
          data-testid="public-trip-score-card"
        >
          <div className="flex items-start justify-between">
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-foreground/60">{score.first_name}&rsquo;s trip</div>
              <div className="font-display text-sm text-foreground/70 mt-1">{score.car_title}</div>
              <div className="font-display text-xs text-foreground/60">{score.trip_date}</div>
            </div>
            <Icon size={32} style={{ color: band.color }} />
          </div>
          <div className="mt-7 flex items-end gap-1.5">
            <div className="font-display text-7xl font-bold tracking-tighter leading-none" style={{ color: band.color }}>
              {score.eco_score}
            </div>
            <div className="font-display text-xl pb-1.5 text-foreground/60">/ 100</div>
          </div>
          <div className="text-base font-display font-semibold mt-1" style={{ color: band.color }}>{band.label}</div>

          <div className="mt-6 grid grid-cols-2 gap-3 text-sm">
            <div className="bg-white/40 rounded-md p-3">
              <div className="flex items-center gap-1.5 text-xs text-foreground/60"><MapPin size={11} /> Distance</div>
              <div className="font-display text-xl font-bold mt-0.5">{score.distance_km} km</div>
            </div>
            <div className="bg-white/40 rounded-md p-3">
              <div className="flex items-center gap-1.5 text-xs text-foreground/60"><Clock size={11} /> Duration</div>
              <div className="font-display text-xl font-bold mt-0.5">{score.duration_min} min</div>
            </div>
            <div className="bg-white/40 rounded-md p-3">
              <div className="flex items-center gap-1.5 text-xs text-foreground/60"><Gauge size={11} /> Avg speed</div>
              <div className="font-display text-xl font-bold mt-0.5">{score.avg_speed_kmh} km/h</div>
            </div>
            <div className="bg-white/40 rounded-md p-3">
              <div className="flex items-center gap-1.5 text-xs text-foreground/60"><Gauge size={11} /> Max speed</div>
              <div className="font-display text-xl font-bold mt-0.5">{score.max_speed_kmh} km/h</div>
            </div>
          </div>
        </div>

        <Card className="border-border p-5 mt-6 flex items-center justify-between gap-3">
          <div>
            <div className="font-display font-semibold">Want your own trip score?</div>
            <div className="text-xs text-muted-foreground">Rent a car on {score.platform_name} and start sharing.</div>
          </div>
          <Button asChild className="bg-accent text-accent-foreground hover:bg-accent/90 btn-lift" data-testid="trip-share-cta">
            <Link to="/cars">Browse cars <ArrowRight size={14} className="ml-1" /></Link>
          </Button>
        </Card>
      </div>
    </div>
  );
}
